import { useMemo } from 'react'

import {
  type UseQueryResult,
  useQueries,
  useQuery,
} from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { normalizeOrgIds, pursuitCountQueryKeys } from '@/hooks/pursuits/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'
import type { Period, PursuitCount } from '@/types/pursuit/Pursuit'

interface SingleOrgArgs {
  orgId: UUID | ''
  orgIds?: never
  period: Period
}

interface MultiOrgArgs {
  orgIds: UUID[]
  orgId?: never
  period: Period
}

type UseActivePursuitCountArgs = SingleOrgArgs | MultiOrgArgs

interface UseActivePursuitCountResult {
  data: PursuitCount | undefined
  isLoading: boolean
  isError: boolean
  errors: Error[]
  failedOrgIds: UUID[]
}

const STALE_TIME_MS = 1000 * 60 * 30

const mergeCounts = (parts: PursuitCount[]): PursuitCount => ({
  total_active: parts.reduce((s, p) => s + p.total_active, 0),
  current_period: parts.reduce((s, p) => s + p.current_period, 0),
  previous_period_same_time: parts.reduce(
    (s, p) => s + p.previous_period_same_time,
    0,
  ),
})

const combineActivePursuitCount = (results: UseQueryResult<PursuitCount>[]) => {
  const parts: PursuitCount[] = []
  for (const r of results) {
    if (r.data) parts.push(r.data)
  }
  return {
    merged: parts.length > 0 ? mergeCounts(parts) : undefined,
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
    errors: results
      .map((r) => r.error)
      .filter((e): e is Error => e instanceof Error),
    errorFlags: results.map((r) => r.isError),
  }
}

export const useActivePursuitCount = (
  args: UseActivePursuitCountArgs,
): UseActivePursuitCountResult => {
  const { accessToken } = useAuthInfoWithThrow()
  const orgIds = normalizeOrgIds(args)
  const isSingle = orgIds.length <= 1
  const singleOrgId = orgIds[0] ?? ''
  const { period } = args

  const singleQuery = useQuery({
    queryKey: pursuitCountQueryKeys.organization({
      orgId: singleOrgId,
      period,
    }),
    queryFn: () => fetchActivePursuitCount(singleOrgId, period, accessToken),
    staleTime: STALE_TIME_MS,
    enabled: isSingle && !!singleOrgId,
  })

  const multi = useQueries({
    queries: isSingle
      ? []
      : orgIds.map((orgId) => ({
          queryKey: pursuitCountQueryKeys.organization({ orgId, period }),
          queryFn: () => fetchActivePursuitCount(orgId, period, accessToken),
          staleTime: STALE_TIME_MS,
          enabled: !!orgId,
        })),
    combine: combineActivePursuitCount,
  })

  const data = useMemo<PursuitCount | undefined>(() => {
    if (isSingle) return singleQuery.data
    return multi.merged
  }, [isSingle, singleQuery.data, multi.merged])

  if (isSingle) {
    return {
      data,
      isLoading: singleQuery.isLoading,
      isError: singleQuery.isError,
      errors: singleQuery.error ? [singleQuery.error as Error] : [],
      failedOrgIds: singleQuery.isError && singleOrgId ? [singleOrgId] : [],
    }
  }

  return {
    data,
    isLoading: multi.isLoading,
    isError: multi.isError,
    errors: multi.errors,
    failedOrgIds: orgIds.filter((_, i) => multi.errorFlags[i]),
  }
}

export const fetchActivePursuitCount = async (
  orgId: UUID | '',
  period: Period,
  accessToken: string,
): Promise<PursuitCount> => {
  if (!orgId) {
    throw new Error('Organization ID is required')
  }
  const response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/pursuits/active/count?period=${period}`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    },
  )
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as PursuitCount
}
